import {
  PriceEditor,
  PriceEditorState,
} from '@/domain/pricing/services/price-editor';
import { useCallback, useRef, useSyncExternalStore } from 'react';
import { usePricingStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/pricing-store';

/**
 * Syncs price input with a variant packs PriceEditor instance from the pricing store.
 *
 * @param variantPackUUID - The UUID of the variant pack to track
 * @returns The current PriceEditorState from the editor, or null if no editor exists
 *
 * @example
 * const editorState = usePriceEditorState(variantPackUUID);
 * const displayValue = editorState?.grossProfitDisplayText ?? '';
 */
export const usePriceEditorState = (
  variantPackUUID: string,
): PriceEditorState | null => {
  const editorStateRef = useRef<PriceEditorState | null>(null);

  return useSyncExternalStore(
    useCallback(
      (cb: () => void) => {
        let editorUnsub: () => void = () => {};
        let subscribedEditor: PriceEditor | undefined;

        // Subscribe to pricing store to ensure we have the latest price editor instance
        const storeUnsub = usePricingStore.subscribe(() => {
          const current = usePricingStore
            .getState()
            .editors.get(variantPackUUID);

          if (current !== subscribedEditor) {
            subscribeToEditor(current);
            cb();
          }
        });

        // Subscribe to price editor for state changes
        const subscribeToEditor = (nextEditor: PriceEditor | undefined) => {
          editorUnsub();

          subscribedEditor = nextEditor;
          editorStateRef.current = nextEditor?.getState() ?? null;

          editorUnsub =
            nextEditor?.subscribe((newState) => {
              editorStateRef.current = newState;
              cb();
            }) ?? (() => {});
        };

        subscribeToEditor(
          usePricingStore.getState().editors.get(variantPackUUID),
        );

        return () => {
          storeUnsub();
          editorUnsub();
        };
      },
      [variantPackUUID],
    ),
    useCallback(() => editorStateRef.current, []),
  );
};
